import { useState, memo, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import { Box, Grid2, Card, CardContent, Typography, Stack, FormControl, FormLabel, Button, TextField, CircularProgress } from '@mui/material';
import { useCommonStyles } from '../../styles/GenericStyle';
import { useResponsiveHeaderHeight } from '../../hooks/useResponsiveHeaderHeight';
import { editSPLotGridCols } from '../../utils/SPLotGridConfig';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/Add';
import { GenericConstant } from '../../utils/GenericConstant';
import { DataGridPro, useGridApiRef, gridVisibleColumnFieldsSelector, type GridSortModel, type GridRowSelectionModel } from '@mui/x-data-grid-pro';
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import { useGetDrillDownSPLotById, useUpdateSplot } from '../../api/vegApl';
import type { SPLotModel, SPLotSummaryModel, MaterialGridModel } from '../../api/vegApl/SPLotModel';
import type { SplotProtocolStepModel } from '../../api/vegApl/SPLotModel';
import StepperCom from './Stepper';
import type { SPLotWorkflowStepStatus } from './Stepper';
import { useExportToExcel } from '../../utils/ExcelExport';
import { addIdIncrement, getCurrentStepName } from '../../utils/HelperFunction';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGlobalState } from "../../contexts/GlobalState";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { greyHeader, greyDark } from "../../colors";
import { CustomizeColumnsPanel } from "../common/CustomizeColumnsPanel";
import { spLotGridColumns } from './SPLotColsConst';
import AddEditSPLotFormDialog from './AddEditSPMatPopup';
import { MtComStyleObj, getDataGridStyles } from '../../styles/MtComStyleObj';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useConfirm } from "../../contexts/confirmDialogContext";
import CreateLabelDialog from './CreateLabelPopup';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { FloatingActionBar } from './FloatingBar';
import MoveMaterialsDialog from './MoveMaterialsDialog';
import { useSessionState } from '../../hooks/useSessionState';
import { usePersistedGridState } from '../../hooks/usePersistedGridState';


export type SPLotViewprops = {
    setMode: (mode: string) => void,
    drillDownParam: SPLotModel | null,
    onClose?: () => void,
    onUnsavedChangesUpdate?: (hasChanges: boolean) => void,
    showArchivedSPLots: boolean

}

export interface SPLotViewRef {
    closingConfirmation: () => Promise<boolean>;
}

export type ColumnVisibility = Record<string, boolean>;
const SPLotView = forwardRef<SPLotViewRef, SPLotViewprops>(({ setMode, drillDownParam, onClose, onUnsavedChangesUpdate, showArchivedSPLots }, ref) => {
    const { remarks, state: { roleSelected = "" }, state: globalState } = useGlobalState();
    const classes = useCommonStyles();
    const headerHeight = useResponsiveHeaderHeight(48);
    const apiRef = useGridApiRef();
    const { applyColumnWidths, onColumnWidthChange, clearColumnWidths } = usePersistedGridState(`splot-view-grid-${drillDownParam?.splot_id ?? 'default'}`);
    const [editSPMatData, setEditSPMatData] = useState<any[]>([]);
    const [open, setOpenMatModel] = useState(false);
    const [mode, setAddMatDiaMode] = useState("add");
    const [currentRow, setCurrentRow] = useState<MaterialGridModel | null>(null);
    const [drillDownSummary, setDrillDownSummary] = useState<SPLotSummaryModel | null>(null);
    const { exportToExcel } = useExportToExcel();
    const [isLoading, setIsLoading] = useState(false);
    const [showCustomizeColumns, setShowCustomizeColumns] = useState(false);
    const [shHideFinalCol, setShHideFinalCol] = useState({});
    const [refreshMatGrid, setRefreshMatGrid] = useState(true);
    const [materialComment, setMaterialComment] = useSessionState(
        `splotView_comment_${drillDownParam?.splot_id ?? ''}`,
        drillDownParam?.comments ?? ""
    );
    const [originalComment, setOriginalComment] = useState(drillDownParam?.comments ?? "");
    const [showCommentMsg, setShowCommentMsg] = useState(false);
    const [showCreateLabelPopup, setShowCreateLabelPopup] = useState(false);
    const [apiLoading, setApiLoading] = useState({
        editSpLot: false
    });
    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => {
        const savedVisibility = localStorage.getItem("spLotColumnVisibility");
        return savedVisibility ? JSON.parse(savedVisibility) : spLotGridColumns;
    });
    const [columnOrder, setColumnOrder] = useState<string[]>(() => {
        const savedOrder = localStorage.getItem("spLotColumnOrder");
        return savedOrder ? JSON.parse(savedOrder) : [];
    });
    const { confirm } = useConfirm();
    const { showSnackbar } = useSnackbar();
    const [SPLotSteps, setSPLotSteps] = useState(null);
    const [SPLotWorkflowSteps, setSPLotWorkflowSteps] = useState<SPLotWorkflowStepStatus>({});
    const [sortModel, setSortModel] = useSessionState<GridSortModel>(
        `splotView_sort_${drillDownParam?.splot_id ?? ''}`,
        []
    );
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [showMoveMaterialsDialog, setShowMoveMaterialsDialog] = useState(false);
    const [moveMaterialRow, setMoveMaterialRow] = useState<MaterialGridModel | null>(null);
    const [moveDialogSource, setMoveDialogSource] = useState<'row' | 'selection'>('selection');

    const selectedMaterialRows = useMemo(() =>
        editSPMatData.filter((row) => selectionModel.includes(row.serialNo)),
        [editSPMatData, selectionModel]
    );

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    }, []);

    // Build sort params expected by the drill_down_splot_by_id API from the grid's sort model
    const buildSortParams = useCallback((model: GridSortModel): { sort_by: string | null; sort_order: 'ASC' | 'DESC' | null } => {
        if (!model || model.length === 0 || !model[0]?.sort) {
            return { sort_by: null, sort_order: null };
        }
        const sortItem = model[0];
        return { sort_by: sortItem.field, sort_order: sortItem.sort === 'asc' ? 'ASC' : 'DESC' };
    }, []);

    const { mutate: getDrillDownData } = useGetDrillDownSPLotById({
        mutation: {
            onSuccess: (data: any) => {
                let materialData = data?.data?.materials ?? []
                setEditSPMatData(addIdIncrement(materialData));
                setDrillDownSummary(data?.data?.summary ?? {});
                let allSteps = data?.data?.splot_protocol_steps ?? [];
                setSPLotSteps(allSteps);
                let currentStep = getCurrentStepName(data?.data?.splot_protocol_steps, data?.data?.current_protocol_step_id)
                const currentStepDetails = (allSteps as SplotProtocolStepModel[])
                    .find((step) => step?.protocol_step_id === data?.data?.current_protocol_step_id);
                const isResourceIdRequired = Boolean(
                    currentStepDetails?.resource_id_required ??
                    currentStepDetails?.resource_required ??
                    currentStepDetails?.is_resource_id_required
                );
                const treatmentRequired = Boolean(currentStepDetails?.treatment_required);
                const equipments = Array.isArray(currentStepDetails?.equipments)
                    ? currentStepDetails?.equipments
                    : [];
                const mappedTreatments = Array.isArray(currentStepDetails?.mapped_treatments)
                    ? currentStepDetails?.mapped_treatments
                    : [];
                let workflowStatus: any = {
                    "current_protocol_step_id": data?.data?.current_protocol_step_id,
                    "current_protocol_step_status": data?.data?.current_protocol_step_status,
                    "current_protocol_step_name": currentStep,
                    "current_workflow_protocol_id": data?.data?.splot_workflow_protocol_id,
                    "resource_id_required": isResourceIdRequired,
                    "treatment_required": treatmentRequired,
                    "equipments": equipments,
                    "mapped_treatments": mappedTreatments,
                }

                setSPLotWorkflowSteps(workflowStatus)
                setMode("drill")
                setIsLoading(false);
            },
            onError: (error: any) => {
                setEditSPMatData([]);
                setIsLoading(false);
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error in getting Material Data';
                showSnackbar({ message: errorMessage, severity: 'error' });

            }
        },
    })

    const { mutate: UpdateSplot } = useUpdateSplot({
        mutation: {
            onSuccess: (data: any) => {
                showSnackbar({ message: data?.data?.message, severity: 'success' });
                setApiLoading(prev => ({ ...prev, editSpLot: false, }));
            },
            onError: (error: any) => {
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error in updating Material';
                showSnackbar({ message: errorMessage, severity: 'error' });
                setIsLoading(false);
                setApiLoading(prev => ({ ...prev, editSpLot: false, }));

            }
        },
    })

    useEffect(() => {
        if (refreshMatGrid) {
            setIsLoading(true);
            const sortParams = buildSortParams(sortModel);
            drillDownParam?.splot_id && getDrillDownData({ splot_id: drillDownParam?.splot_id ?? "", sort_by: sortParams.sort_by, sort_order: sortParams.sort_order });
            setRefreshMatGrid(false);
        }

    }, [drillDownParam, getDrillDownData, refreshMatGrid])

    // Re-fetch from the API (server-side sort) whenever the user changes the column sort
    const isFirstSortRef = useRef(true);
    useEffect(() => {
        if (isFirstSortRef.current) {
            isFirstSortRef.current = false;
            return;
        }
        if (!drillDownParam?.splot_id) return;

        setIsLoading(true);
        const sortParams = buildSortParams(sortModel);
        getDrillDownData({ splot_id: drillDownParam.splot_id, sort_by: sortParams.sort_by, sort_order: sortParams.sort_order });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortModel])

    const edidSPLoTMatData = (params: any) => {
        let currentRow: any = { ...params?.row };
        let comment = params?.row?.comments;
        let remark_id = remarks.find((ele) => ele?.remark_name == comment);
        currentRow.comments = remark_id?.remark_id ?? "";
        setOpenMatModel(true); setAddMatDiaMode("Edit"); setCurrentRow(currentRow);
    }

    const handleMoveToSPLot = useCallback((params: any) => {
        setMoveDialogSource('row');
        setMoveMaterialRow(params?.row ?? null);
        setShowMoveMaterialsDialog(true);
    }, []);

    const materialsToMove = useMemo(() => {
        if (moveDialogSource === 'row') {
            return moveMaterialRow ? [moveMaterialRow] : [];
        }

        if (moveDialogSource === 'selection' && selectedMaterialRows.length > 0) {
            return selectedMaterialRows;
        }

        if (moveMaterialRow) {
            return [moveMaterialRow];
        }

        return [];
    }, [selectedMaterialRows, moveMaterialRow, moveDialogSource]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setColumnVisibility((prev) => ({ ...prev, [name]: checked }));
    }, []);

    const orderedColumns = useMemo(() => {
        const defaultCols = editSPLotGridCols(edidSPLoTMatData, classes, roleSelected, showArchivedSPLots, sortModel, handleMoveToSPLot);
        let orderedCols = [...defaultCols];

        if (columnOrder.length > 0) {
            orderedCols = columnOrder
                .map(field => defaultCols.find(col => col.field === field))
                .filter((col): col is any => col !== undefined);

            const orderedFields = new Set(columnOrder);
            const newColumns = defaultCols.filter(col => !orderedFields.has(col.field));
            orderedCols = [...orderedCols, ...newColumns];
        }

        return applyColumnWidths(orderedCols);
    }, [columnOrder, edidSPLoTMatData, classes, roleSelected, sortModel, applyColumnWidths, handleMoveToSPLot]);

    const headerToFieldMap = useMemo(() => {
        const map: Record<string, string> = {};
        orderedColumns.forEach((col: any) => {
            if (typeof col?.headerName === 'string' && col?.field) {
                map[col.headerName] = String(col.field);
            }
        });
        return map;
    }, [orderedColumns]);

    const handleToggleCustomize = useCallback(() => setShowCustomizeColumns(prev => !prev), []);

    const handleColumnOrderChange = useCallback(() => {
        const visibleColumnFields = gridVisibleColumnFieldsSelector(apiRef);
        setColumnOrder(visibleColumnFields);
        localStorage.setItem("spLotColumnOrder", JSON.stringify(visibleColumnFields));
    }, [apiRef, shHideFinalCol]);

    const handleResetToDefault = useCallback(() => {
        setColumnVisibility(spLotGridColumns);
        setColumnOrder([]);
        clearColumnWidths();
        localStorage.removeItem("spLotColumnVisibility");
        localStorage.removeItem("spLotColumnOrder");
    }, [clearColumnWidths]);

    const saveColumnPreferences = useCallback(() => {
        localStorage.setItem("spLotColumnVisibility", JSON.stringify(columnVisibility));
        localStorage.setItem("spLotColumnOrder", JSON.stringify(columnOrder));
        setShowCustomizeColumns(false);
    }, [columnVisibility, columnOrder]);

    useEffect(() => {
        localStorage.setItem("spLotColumnVisibility", JSON.stringify(columnVisibility));
        let tmpArray: any = {};

        for (const key in columnVisibility) {
            let foundKey = headerToFieldMap[key] ?? "";
            tmpArray[foundKey] = columnVisibility[key]
        }
        setShHideFinalCol(tmpArray);

    }, [columnVisibility, headerToFieldMap]);


    useEffect(() => {
        const hasChanges = materialComment !== originalComment;
        onUnsavedChangesUpdate?.(hasChanges);
    }, [materialComment, originalComment, onUnsavedChangesUpdate]);

    const closingConfirmation = async (): Promise<boolean> => {
        const hasChanges = materialComment !== originalComment;
        if (hasChanges) {
            const ok: any = await confirm({
                title: GenericConstant.CONFIRMATION,
                description: GenericConstant.UNSAVECHANGEPOPOP,
                confirmText: "Yes",
                cancelText: "No",
                severity: "error",
            });
            if (ok) {
                onClose?.();
                setMode("view");
                return true;
            }
            return false;
        }
        else {
            onClose?.();
            setMode("view");
            return true;
        }
    }

    useImperativeHandle(ref, () => ({
        closingConfirmation
    }), [materialComment, originalComment]);

    const editSPLot = useCallback(() => {

        if (materialComment.length) {
            let req: any = {};
            req.splot_id = drillDownParam?.splot_id;
            req.updated_by = globalState?.user_id_db
            req.comments = materialComment;
            setApiLoading(prev => ({ ...prev, editSpLot: true }));
            UpdateSplot({ data: req }, {
                onSuccess: () => {
                    setOriginalComment(materialComment);
                }
            });
        }
        else {
            setShowCommentMsg(true)
        }
    }, [materialComment]);

    const NoDataMsg = () => {
        return (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">No Materials found</Typography>
            </Box>
        );
    }

    function checkifDisabled(): boolean {
        if (editSPMatData.length === 0) return false;
        return editSPMatData.some((ele: any) => ele.material_status?.toLowerCase() === 'processing' || ele.material_status?.toLowerCase() === 'registered');
    }

    return (
        <>
            <FullScreenActionLoader open={isLoading} />
            <Box width={"100%"}>
                <Typography component="span" sx={{ cursor: "pointer" }} className={classes.labelNoto16Bold700} onClick={() => { closingConfirmation() }} >
                    <ArrowBackIcon sx={{
                        verticalAlign: "middle", transform: 'scaleX(1.3)',
                        transformOrigin: 'center left',
                        marginRight: 1,

                    }} />Back to SP Lots
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' }, mt: 2, gap: { xs: 1.5, sm: 0 } }}>
                    <Typography component="div">
                        <Stack direction="column" spacing={0.2} flexWrap="wrap">
                            <Typography component="span" sx={{ fontSize: { xs: '20px', sm: '24px' } }} className={classes.labelNoto24Bold400}>
                                SP Lot Materials: {drillDownParam?.splot_id ?? ""}
                            </Typography>
                            <Typography component="span" color="text.secondary" className={classes.labelInter14Bold400}>
                                {editSPMatData?.length ?? ""} materials
                            </Typography>
                        </Stack>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        {["S2S-Lead", "Admin", "Technician"].includes(roleSelected) && !showArchivedSPLots && <Button disableRipple disabled={checkifDisabled()} className={checkifDisabled() ? '' : classes.greenButton} startIcon={<AddIcon />}
                            sx={(theme) => ({
                                px: 2,
                                ...MtComStyleObj(theme).disabledGreenBtnStyle, padding: "10px 12px"
                            })}
                            onClick={() => { setOpenMatModel(true); setAddMatDiaMode("Add"); setCurrentRow(null); }}
                        >Add  Material</Button>}
                    </Box>
                </Box>

                <Card elevation={0} variant="outlined"
                    sx={{
                        width: '100%',
                        px: { xs: 1.5, sm: 3 },
                        mt: 2,
                    }}>
                    <CardContent sx={{ pl: 0, pb: 0 }}>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: "100%" }}>
                            <Typography gutterBottom className={classes.labelNoto20Bold600}>
                                Edit SP Lot: {drillDownParam?.splot_id ?? ""}
                            </Typography>
                            <IconButton aria-label="close" onClick={() => { closingConfirmation() }}
                                sx={(theme) => ({ ...MtComStyleObj(theme).dialogCloseButton })}><CloseIcon />
                            </IconButton>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                            <Typography component="div" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography component="span" color="text.secondary" className={classes.labelNoto14Bold400} >Crop: </Typography>
                                <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} sx={{ ml: 0.5 }}>{drillDownParam?.cropName}</Typography>
                            </Typography>
                            <Typography component="div" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography component="span" color="text.secondary" className={classes.labelNoto14Bold400} >Trial IDs: </Typography>
                                <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} sx={{ ml: 0.5 }}>{drillDownParam?.trial_id}</Typography>
                            </Typography>
                            <Typography component="div" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography component="span" color="text.secondary" className={classes.labelNoto14Bold400} >Total Materials: </Typography>
                                <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} sx={{ ml: 0.5 }}>{drillDownSummary?.total_materials ?? ""}</Typography>
                            </Typography>
                            <Typography component="div" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography component="span" color="text.secondary" className={classes.labelNoto14Bold400}>Total Weight: </Typography>
                                <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} sx={{ ml: 0.5 }}>
                                    {drillDownSummary?.total_weight_g
                                        ? `${drillDownSummary.total_weight_g} g`
                                        : ""}
                                </Typography>
                            </Typography>
                            <Typography component="div" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography component="span" color="text.secondary" className={classes.labelNoto14Bold400}>Total Fruits: </Typography>
                                <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} sx={{ ml: 0.5 }}>{drillDownSummary?.total_fruit_count ?? ""}</Typography>
                            </Typography>
                            <Typography component="div" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography component="span" color="text.secondary" className={classes.labelNoto14Bold400}>Total Bags: </Typography>
                                <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} sx={{ ml: 0.5 }}>{drillDownSummary?.total_bags ?? ""}</Typography>
                            </Typography>
                        </Stack>
                    </CardContent>
                    <Stack direction="row" flexWrap="wrap" sx={{ mt: { xs: '12px !important', sm: '16px !important' } }}>
                        <FormControl size="small">
                            <FormLabel className={classes.labelInter14Bold400} sx={{ color: greyDark }}>Comments</FormLabel>
                            <TextField placeholder="Enter comment" variant="outlined"
                                value={materialComment ?? ""}
                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).textFocusHoverStyle,
                                    ...MtComStyleObj(theme).textBoxValueStyle,

                                })}
                                slotProps={{
                                    input: {
                                        sx: { height: 40, width: { xs: '100%', sm: 290 }, minWidth: 200, mt: 1 }
                                    },
                                    formHelperText: { sx: { ml: 0 } }
                                }} onChange={(e) => { setMaterialComment(e.target.value), setShowCommentMsg(false) }}
                                disabled={!["S2S-Lead", "Technician", "Admin"].includes(roleSelected) || apiLoading.editSpLot || showArchivedSPLots}
                                error={!!showCommentMsg}
                                helperText={showCommentMsg ? 'Please Provide comment' : ' '}
                            />
                        </FormControl>
                        {!showArchivedSPLots && <Box sx={{ marginLeft: "8px !important", display: "flex", alignItems: "center", mt: "5px" }}>
                            <Button variant="contained" disableRipple disabled={!["S2S-Lead", "Technician", "Admin"].includes(roleSelected) || apiLoading.editSpLot} className={classes.greenButton} sx={() => ({
                                padding: "10px 12px"

                            })} onClick={editSPLot} loading={apiLoading.editSpLot} loadingPosition="start">Save</Button>

                        </Box>}
                    </Stack>
                    <StepperCom stArray={SPLotSteps ?? []} SPLotWorkflowSteps={SPLotWorkflowSteps} status={""} drillDownParam={drillDownParam} setRefreshDrillDown={setRefreshMatGrid} showArchivedSPLots={showArchivedSPLots} />
                </Card >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1, width: '100%' }}>
                    <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Box sx={{ position: 'relative' }}>
                            <Button variant="contained" disableRipple className={classes.whiteButton} startIcon={<SettingsOutlinedIcon />}
                                sx={(theme) => ({
                                    ...MtComStyleObj(theme).disabledWhiteBtnStyle, padding: "10px 12px"
                                })}
                                onClick={handleToggleCustomize}>Customize columns</Button>
                            <CustomizeColumnsPanel
                                show={showCustomizeColumns}
                                columnVisibility={columnVisibility}
                                onResetToDefault={handleResetToDefault}
                                onColumnChange={handleChange}
                                saveColumnPreferences={saveColumnPreferences}
                                elevation={3} />
                        </Box>
                        <Button variant="contained" disableRipple className={classes.whiteButton} startIcon={<FileDownloadOutlinedIcon />}
                            sx={(theme) => ({
                                ...MtComStyleObj(theme).disabledWhiteBtnStyle, padding: "10px 12px"
                            })}
                            onClick={() => exportToExcel(editSPMatData, `SPLotMaterials_${drillDownParam?.splot_id}`, ["serialNo"])}
                        >Download</Button>
                    </Box>
                </Box>
                <Grid2 size={{ xs: 12, sm: 6, md: 12 }} sx={{ mt: 2, minHeight: showCustomizeColumns ? '500px' : 'auto' }}>
                    {isLoading ? <Box
                        sx={{
                            display: 'flex', placeItems: 'center', justifyContent: 'center', height: '100%', p: 5
                        }}> <CircularProgress size={32} />
                    </Box>
                        :
                        <Box sx={{
                            width: '100%', mt: 2, minHeight: "100px",
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: "bold", color: 'rgba(0,0,0,0.85)', fontSize: 16
                            },
                        }}>
                            <DataGridPro
                                key={`splot-grid-${columnOrder.join('-')}`}
                                apiRef={apiRef}
                                pageSizeOptions={GenericConstant.SPLOTTPAGINATION}
                                columns={orderedColumns}
                                checkboxSelection
                                rows={Array.isArray(editSPMatData) ? editSPMatData : []}
                                pagination columnVisibilityModel={shHideFinalCol}
                                columnHeaderHeight={headerHeight} getRowId={(row) => row?.serialNo ?? ""}
                                onColumnOrderChange={handleColumnOrderChange}
                                onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                                rowSelectionModel={selectionModel}
                                sortingMode="server"
                                sortModel={sortModel}
                                onSortModelChange={handleSortModelChange}
                                onColumnWidthChange={onColumnWidthChange}
                                slots={{ noRowsOverlay: NoDataMsg }}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 100, page: 0 } },
                                    ...(columnOrder.length > 0 && {
                                        columns: {
                                            orderedFields: columnOrder
                                        }
                                    })
                                }}
                                slotProps={{
                                    baseCheckbox: {
                                        checkedIcon: <Box component="img" alt="" src="icons/CheckBox.svg" />,
                                        icon: <CheckBoxOutlineBlankRoundedIcon fontSize="small" />,
                                        sx: {
                                            "&.Mui-checked": { color: '#000000' },
                                            color: '#000000',
                                            fontSize: 20,
                                            "& svg": { fontSize: 20 },
                                        },
                                    },
                                }}
                                sx={(theme) => getDataGridStyles(theme, {
                                    headerColor: greyHeader,
                                    fillerColor: greyHeader,
                                    maxHeight: 'auto',
                                })}
                            />
                        </Box>
                    }
                </Grid2>
            </Box>

            <AddEditSPLotFormDialog openAddEditMatModal={open} setaddEditMatModal={setOpenMatModel} mode={mode} materialRowData={currentRow} drillDownCurrRow={drillDownParam} setRefreshMatGrid={setRefreshMatGrid} />
            <CreateLabelDialog open={showCreateLabelPopup} drillDownCurrRow={drillDownParam} setCreateLableModal={setShowCreateLabelPopup} onSuccess={() => setRefreshMatGrid(true)} />
            <FloatingActionBar
                open={selectionModel.length >= 2}
                selectedCount={selectionModel.length}
                onClose={() => setSelectionModel([])}
                type="material"
                hideActions
                onMoveMaterials={() => {
                    setMoveDialogSource('selection');
                    setMoveMaterialRow(null);
                    setShowMoveMaterialsDialog(true);
                }}
            />
            <MoveMaterialsDialog
                open={showMoveMaterialsDialog}
                onClose={() => {
                    setShowMoveMaterialsDialog(false);
                    setMoveMaterialRow(null);
                    setMoveDialogSource('selection');
                }}
                selectedMaterials={materialsToMove}
                currentSplotId={drillDownParam?.splot_id ?? ""}
                currentSplotStatus={drillDownParam?.status ?? ""}
                currentSplotCropId={drillDownParam?.crop ?? ""}
                currentSplotCropName={drillDownParam?.cropName ?? ""}
                onSuccess={() => {
                    setSelectionModel([]);
                    setMoveMaterialRow(null);
                    setMoveDialogSource('selection');
                    setRefreshMatGrid(true);
                }}
            />

        </>
    );
});
export default memo(SPLotView);
